import type { Product } from '../types';
import { API_BASE_URL } from '../config/api';

export interface CartApiItem {
  cartId?: string;
  bookId?: string;
  productId?: string;
  product?: unknown;
  quantity?: unknown;
  price?: unknown;
  edition?: unknown;
  isPOD?: unknown;
  isDigital?: unknown;
}

interface CartServiceConfig {
  baseUrl: string;
  tokenKey: string;
  userKey: string;
}

const defaultCartServiceConfig: CartServiceConfig = {
  baseUrl: API_BASE_URL,
  tokenKey: 'haapa.authToken',
  userKey: 'haapa.currentUser',
};

class CartService {
  private baseUrl: string;
  private tokenKey: string;
  private userKey: string;

  constructor(config = defaultCartServiceConfig) {
    this.baseUrl = config.baseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private asString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private asNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return undefined;
  }

  private buildHeaders(withJson = false): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    if (withJson) {
      headers['Content-Type'] = 'application/json';
    }

    if (typeof window === 'undefined') {
      return headers;
    }

    const token = window.localStorage.getItem(this.tokenKey);
    const rawUser = window.localStorage.getItem(this.userKey);

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (rawUser) {
      try {
        const parsedUser = this.asRecord(JSON.parse(rawUser));
        const userId = this.asString(parsedUser.id);
        const role = this.asString(parsedUser.role);
        if (userId) {
          headers['x-user-id'] = userId;
        }
        if (role) {
          headers['x-user-role'] = role;
        }
      } catch {
        // Ignore malformed user cache
      }
    }

    return headers;
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    const message =
      this.asString(record.message) ||
      this.asString(dataRecord.message) ||
      this.asString(record.error) ||
      this.asString(dataRecord.error);

    return message ?? fallback;
  }

  private async parseResponse(response: Response): Promise<unknown> {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const fallback = `Request failed with status ${response.status}`;
      throw new Error(this.toErrorMessage(data, fallback));
    }

    return data;
  }

  private normalizeCartItems(payload: unknown): CartApiItem[] {
    const payloadRecord = this.asRecord(payload);
    const dataRecord = this.asRecord(payloadRecord.data);
    const cartRecord = this.asRecord(payloadRecord.cart);

    const candidates = [
      this.asArray(payloadRecord.items),
      this.asArray(dataRecord.items),
      this.asArray(cartRecord.items),
      this.asArray(payload),
    ];

    for (const candidate of candidates) {
      if (candidate.length > 0) {
        return candidate.map((item) => {
          const itemRecord = this.asRecord(item);
          const productRecord = this.asRecord(itemRecord.product ?? itemRecord.book);
          const cartId =
            this.asString(itemRecord.cartId) ||
            this.asString(itemRecord.cart_id) ||
            this.asString(itemRecord.id);
          const bookId =
            this.asString(itemRecord.bookId) ||
            this.asString(itemRecord.book_id) ||
            this.asString(productRecord.id);
          const productId =
            this.asString(itemRecord.productId) ||
            this.asString(itemRecord.product_id) ||
            bookId ||
            this.asString(productRecord._id);
          return {
            cartId,
            bookId,
            productId,
            product: (itemRecord.product ?? itemRecord.book) as Product | unknown,
            quantity: this.asNumber(itemRecord.quantity) ?? this.asNumber(itemRecord.qty) ?? 1,
            price: this.asNumber(itemRecord.price),
            edition: this.asString(itemRecord.edition),
          };
        });
      }
    }

    return [];
  }

  async getCart(): Promise<CartApiItem[]> {
    const candidates = [`${this.baseUrl}/cart`, `${this.baseUrl}/cart/items`];

    for (const endpoint of candidates) {
      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: this.buildHeaders(),
        });

        if (response.status === 404 || response.status === 500) {
          continue;
        }

        const payload = await this.parseResponse(response);
        return this.normalizeCartItems(payload);
      } catch {
        // Continue to next candidate or return empty
      }
    }

    return [];
  }

  async addItem(
    productId: string,
    quantity: number,
    options?: { edition?: 'digital' | 'print' },
  ): Promise<CartApiItem[]> {
    const numericBookId = Number(productId);
    const postCandidates: Array<{ url: string; body: Record<string, unknown> }> = [
      ...(Number.isInteger(numericBookId) && numericBookId > 0
        ? [{ url: `${this.baseUrl}/cart`, body: { bookId: numericBookId, quantity } }]
        : []),
      { url: `${this.baseUrl}/cart/items`, body: { productId, quantity, edition: options?.edition } },
    ];

    for (const candidate of postCandidates) {
      try {
        const response = await fetch(candidate.url, {
          method: 'POST',
          headers: this.buildHeaders(true),
          body: JSON.stringify(candidate.body),
        });

        if (response.status === 404 || response.status === 500) {
          continue;
        }

        const payload = await this.parseResponse(response);
        if (candidate.url.endsWith('/cart')) {
          return this.getCart();
        }
        return this.normalizeCartItems(payload);
      } catch {
        // Continue to next candidate
      }
    }

    // Fallback to local cart simulation or just return empty if backend is broken
    return this.getCart();
  }

  async updateItem(productId: string, quantity: number): Promise<CartApiItem[]> {
    const normalizedQuantity = Math.max(1, Math.floor(quantity) || 1);
    const cartItems = await this.getCart().catch(() => []);

    const matchingItem = cartItems.find(
      (item) => item.productId === productId || item.bookId === productId || item.cartId === productId,
    );

    const matchingCartId = matchingItem?.cartId;
    const matchingBookId = Number(matchingItem?.bookId ?? matchingItem?.productId ?? productId);
    const currentQuantity = Number(matchingItem?.quantity ?? 1);

    // Backend supports incrementing quantity via POST /cart and deleting via DELETE /cart/:cartId.
    // To support setting an exact quantity without adding new endpoints:
    // - If increasing: POST the delta
    // - If decreasing: delete the existing row then recreate with the desired quantity
    if (!Number.isInteger(matchingBookId) || matchingBookId <= 0) {
      throw new Error('Unable to update cart item: invalid book id.');
    }

    if (!matchingCartId) {
      // No cart row id; safest is to just add (will create or increment).
      await this.addItem(String(matchingBookId), normalizedQuantity);
      return this.getCart().catch(() => []);
    }

    if (normalizedQuantity === currentQuantity) {
      return cartItems;
    }

    if (normalizedQuantity > currentQuantity) {
      const delta = normalizedQuantity - currentQuantity;
      await this.addItem(String(matchingBookId), delta);
      return this.getCart().catch(() => []);
    }

    // Decrease
    await this.removeItem(matchingCartId);
    await this.addItem(String(matchingBookId), normalizedQuantity);
    return this.getCart().catch(() => []);
  }

  async removeItem(productId: string): Promise<CartApiItem[]> {
    const cartItems = await this.getCart().catch(() => []);
    const matchingCartItem = cartItems.find(
      (item) => item.productId === productId || item.bookId === productId || item.cartId === productId,
    );
    const deleteCandidates = [
      ...(matchingCartItem?.cartId ? [`${this.baseUrl}/cart/${encodeURIComponent(matchingCartItem.cartId)}`] : []),
      `${this.baseUrl}/cart/items/${productId}`,
    ];
    let lastError: Error | null = null;

    for (const endpoint of deleteCandidates) {
      try {
        const response = await fetch(endpoint, {
          method: 'DELETE',
          headers: this.buildHeaders(),
        });
        await this.parseResponse(response);
        return this.getCart().catch(() => []);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Failed to remove item from cart');
      }
    }

    throw lastError ?? new Error('Failed to remove item from cart');
  }
}

export const cartService = new CartService();
